import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useMedia } from 'react-use';
import { Dialog, Transition } from '@headlessui/react';
import { XIcon } from 'lucide-react';
import { KitDocumentModel } from './KitModels';
import FilePreviewer from './FilePreviewer';

export interface ViewMediaModelProps {
  kit_document: KitDocumentModel;
  dialogCloseHandler: () => void;
}


export default function ViewMediaModel({ kit_document, dialogCloseHandler }: ViewMediaModelProps) {
  const [kitDocumentSelected, setKitDocumentSelected] = useState<KitDocumentModel | null>(null);

  const [showDialog, setShowDialog] = useState(true);
  const initialFocusRef = useRef(null);
  
  useEffect(() => {
    setKitDocumentSelected(kit_document);
  }, [kit_document]);
  const isMobileScreen = useMedia('(max-width: 1024px)', false);

  const onCloseDialog = (e?: React.MouseEvent | KeyboardEvent | any) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    dialogCloseHandler();
    setShowDialog(false);
  };

  // Escape close popup isolated
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();
        onCloseDialog(event);
      }
    };
    // Use capture phase to intercept the Escape key before Headless UI's listeners do!
    document.addEventListener('keydown', handleEscKey, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleEscKey, { capture: true });
    };
  }, []);

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {
          onCloseDialog();
        }}
        initialFocus={initialFocusRef}
      >
        {/* Backdrop Transition (Full-screen dark overlay) */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          {/* Headless UI Overlay is crucial for closing on outside click */}
          <div className="fixed inset-0 bg-customGray" />
        </Transition.Child>

        {/* Dialog Panel (where the content lives) */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center px-4 pt-16">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="relative w-full flex flex-col bg-white rounded-md box-shadow-0-0-50 overflow-hidden"
                style={{
                  //  width: '90vw',
                  // maxWidth: '47rem',
                  maxHeight: '90vh',
                }}
              >
                <div
                  className="flex w-full flex-col flex-grow justify-start items-start"
                  ref={initialFocusRef}
                  tabIndex={-1}
                >
                  <div className="contains flex w-full"
                    style={{
                  //  width: '90vw',
                  // maxWidth: '47rem',
                  height: '90vh',
                }}>
                    {kitDocumentSelected && (
                      <FilePreviewer
                        file_url={kitDocumentSelected.file_url}
                        poster_url={kitDocumentSelected.cropped_file_url}
                        object_fit='object-contain'
                        file_name={kitDocumentSelected.file_name}
                        file_content_type={kitDocumentSelected.file_content_type}
                        dynamicHeight={false}
                        showControl={true}
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        onCloseDialog();
                      }}
                      className="w-11 h-11 absolute border border-white top-5 right-5 z-50 text-white transition rounded-full"
                      style={{
                        backgroundColor: '#00000033',
                      }}
                      aria-label="Close image viewer"
                    >
                      <XIcon className="w-11 h-11" />
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}